// The destination catalog: one source of truth for every destination-stable
// fact about a destination type.
//
// This module is deliberately IMPORT-FREE. The backend cannot import across
// tsconfig's rootDir ("src") and the frontend build does not include src/, so
// a pure module is the only thing both sides can read. Everything that used
// to hand-maintain a parallel list of destination types now derives from
// DESTINATION_CATALOG, because a list kept by hand in forty-odd places is a
// list that will disagree with itself.
//
// WHAT BELONGS HERE: facts that are true of a destination wherever you look
// at it -- its names, its help anchor, its release stage, what the
// destination-down email calls its credential.
//
// WHAT DOES NOT: anything about a PAIR of things. Overlap advice is a
// property of the (other app, destination) pair and lives on the registry
// entry in src/lib/installed-apps.ts, because two apps overlapping the same
// destination can need opposite advice (Shopify's Google & YouTube channel
// versus the third-party Google app, both on google_ads). Anything about HOW
// a destination sends belongs on its adapter.

export const DESTINATION_TYPES = [
  "meta_capi",
  "ga4",
  "klaviyo",
  "tiktok",
  "google_ads",
  "pinterest",
  // Added 2026-10-18, all at release "hidden": code landed, live-account
  // checks not done. See DESTINATION_PREVIEW_SHOP_DOMAINS.
  "microsoft_ads",
  "snapchat",
  "reddit",
  "openai_ads",
  "attentive",
] as const;

export type DestinationType = (typeof DESTINATION_TYPES)[number];

/**
 * How far along a destination is, and therefore who may use it.
 *
 * - `hidden`: usable only on the shops in DESTINATION_PREVIEW_SHOP_DOMAINS.
 *   Excluded from supported-type lists and from destination-down alerting, so
 *   a half-wired destination cannot email a merchant.
 * - `early_access`: visible to everyone, labelled. ("beta" is NOT the word:
 *   it already means the comped plan in Billing.tsx.)
 * - `ga`: visible, unlabelled.
 */
export type DestinationRelease = "hidden" | "early_access" | "ga";

/**
 * What the destination-down email needs in order to tell a merchant how to
 * fix their own credentials.
 *
 * The SHAPE carries the policy, so the email cannot be wrong about a
 * destination:
 * - `paste`: the fix is pasting a fresh credential, which the email names.
 * - `oauth`: the fix is signing in again (Google Ads), so there is nothing
 *   to paste and the email must not say "paste".
 * - field absent: the destination never emails the merchant about
 *   credentials. GA4 is the only one, because GA4 refuses no credentials:
 *   every GA4 credentials failure is our own fault, so mailing the merchant
 *   about it would be blaming them for our bug.
 */
export type DestinationNotice =
  | {
      kind: "paste";
      /** The saved credential, as the email names it. The card's field says the same. */
      credentialName: string;
      /** What that credential points at, as the email names it. */
      assetName: string;
      /** This destination can save a test event code, which also has to be cleared. */
      testEventCode: boolean;
      /**
       * Whether this platform's credential can expire on its own. Drives one
       * clause of the down-alert email, because guessing wrong there sends a
       * merchant hunting for an expiry date that does not exist.
       *
       * Default true, which is the common case: Meta, TikTok, Pinterest and
       * Google all expire or rotate. Snapchat's Conversions API token does
       * not expire on a schedule, and its own card says so, so the email must
       * not contradict the card.
       */
      credentialExpires?: boolean;
    }
  | { kind: "oauth" };

export interface DestinationCatalogEntry {
  /** Full product name. Modal and card titles. */
  label: string;
  /** Compact name for the hub nodes and the dashboard. */
  short: string;
  /** The name used mid-sentence, in prose and in emails. */
  name: string;
  /**
   * The X in an adapter's `no X mapping for event_name=...` skip message.
   * The frontend turns that message back into a sentence by matching this,
   * lowercased, so the token and the adapter string must agree -- which
   * destination.test.ts checks against the adapters themselves.
   */
  skipToken: string;
  /** Slug on datacrow.app/help, without the "#". */
  helpAnchor: string;
  release: DestinationRelease;
  /**
   * `core` destinations get their own onboarding step; `more` ones sit behind
   * the collapsible. A `more` entry must carry a `pitch`.
   */
  group: "core" | "more";
  /**
   * The half-sentence after the name on the onboarding collapsible, so the
   * label reads "<name>: <pitch>". Only `more` destinations render one.
   */
  pitch?: string;
  notice?: DestinationNotice;
}

// Annotated rather than `as const satisfies`: the annotation still errors on
// a missing type, a missing required field or a typo'd one, and it keeps the
// uniform entry shape. `as const` would narrow each entry to its own literal
// type, so `catalog[t].notice` would not type-check for the one entry that
// omits it and `release !== "hidden"` would read as comparing two constants.
export const DESTINATION_CATALOG: Record<DestinationType, DestinationCatalogEntry> = {
  meta_capi: {
    label: "Meta Conversions API",
    short: "Meta CAPI",
    name: "Meta",
    skipToken: "Meta",
    helpAnchor: "connecting-meta-conversions-api",
    release: "ga",
    group: "core",
    notice: { kind: "paste", credentialName: "access token", assetName: "pixel", testEventCode: true },
  },
  ga4: {
    label: "Google Analytics 4",
    short: "GA4",
    name: "GA4",
    skipToken: "GA4",
    helpAnchor: "connecting-your-other-destinations",
    release: "ga",
    group: "core",
    // No `notice` on purpose: see DestinationNotice. GA4 never emails the
    // merchant about credentials.
  },
  klaviyo: {
    label: "Klaviyo",
    short: "Klaviyo",
    name: "Klaviyo",
    skipToken: "klaviyo",
    helpAnchor: "connecting-your-other-destinations",
    release: "ga",
    group: "more",
    pitch: "email + SMS flows on accurate signals",
    notice: {
      kind: "paste",
      credentialName: "private API key",
      assetName: "Klaviyo account",
      testEventCode: false,
    },
  },
  tiktok: {
    label: "TikTok Events API",
    short: "TikTok",
    name: "TikTok",
    skipToken: "tiktok",
    helpAnchor: "connecting-your-other-destinations",
    release: "ga",
    group: "more",
    pitch: "server-side conversions for TikTok ads",
    notice: { kind: "paste", credentialName: "access token", assetName: "pixel", testEventCode: true },
  },
  google_ads: {
    label: "Google Ads",
    short: "Google Ads",
    name: "Google Ads",
    skipToken: "google-ads",
    helpAnchor: "connecting-your-other-destinations",
    release: "ga",
    group: "more",
    pitch: "enhanced conversions for web",
    // Signs in again rather than pasting anything.
    notice: { kind: "oauth" },
  },
  microsoft_ads: {
    label: "Microsoft Ads",
    short: "Microsoft Ads",
    name: "Microsoft Ads",
    skipToken: "microsoft-ads",
    helpAnchor: "connecting-your-other-destinations",
    release: "hidden",
    group: "more",
    pitch: "server-side conversions for Microsoft search ads",
    notice: {
      kind: "paste",
      // Microsoft's own wording, and what the card's field says: the token
      // is minted behind "Use Conversions API" and copied from there. "API
      // token" alone sent a merchant hunting at the account level.
      credentialName: "Conversions API token",
      assetName: "UET tag",
      testEventCode: false,
    },
  },
  snapchat: {
    label: "Snapchat Conversions API",
    short: "Snapchat",
    name: "Snapchat",
    skipToken: "snapchat",
    helpAnchor: "connecting-your-other-destinations",
    release: "hidden",
    group: "more",
    pitch: "server-side conversions for Snap ads",
    notice: {
      kind: "paste",
      // Snapchat's own name for it, and what the card's field says. "access
      // token" sent a merchant looking in the OAuth Apps list instead of
      // under Conversions API Tokens.
      credentialName: "Conversions API token",
      assetName: "pixel",
      testEventCode: false,
      // Snap tokens do not expire on a schedule; deleting one in Ads Manager
      // revokes it.
      credentialExpires: false,
    },
  },
  reddit: {
    label: "Reddit Conversions API",
    short: "Reddit",
    name: "Reddit",
    skipToken: "reddit",
    helpAnchor: "connecting-your-other-destinations",
    release: "hidden",
    group: "more",
    pitch: "server-side conversions for Reddit ads",
    notice: {
      kind: "paste",
      // Reddit's own name for it, and what the card's field says.
      credentialName: "conversion access token",
      assetName: "pixel",
      // Reddit's own test ID, stored like Meta's and TikTok's test codes, so
      // a reconnect has to clear it too.
      testEventCode: true,
      // "A conversion access token is a non-expiring secure key." Same
      // situation as Snapchat: the card says it does not expire, so the
      // down-alert email must not tell the merchant it probably did.
      credentialExpires: false,
    },
  },
  openai_ads: {
    label: "ChatGPT Ads",
    short: "ChatGPT Ads",
    name: "ChatGPT Ads",
    skipToken: "openai-ads",
    helpAnchor: "connecting-your-other-destinations",
    release: "hidden",
    group: "more",
    pitch: "conversions for ads inside ChatGPT",
    notice: {
      kind: "paste",
      credentialName: "conversions API key",
      assetName: "pixel",
      testEventCode: false,
    },
  },
  attentive: {
    label: "Attentive",
    short: "Attentive",
    name: "Attentive",
    skipToken: "attentive",
    helpAnchor: "connecting-your-other-destinations",
    release: "hidden",
    group: "more",
    pitch: "SMS flows on accurate purchase signals",
    notice: {
      kind: "paste",
      credentialName: "private API key",
      assetName: "Attentive account",
      testEventCode: false,
    },
  },
  pinterest: {
    label: "Pinterest Conversions API",
    short: "Pinterest",
    name: "Pinterest",
    skipToken: "pinterest",
    helpAnchor: "connecting-your-other-destinations",
    release: "ga",
    group: "more",
    pitch: "conversions API for Pinterest ads",
    notice: {
      kind: "paste",
      credentialName: "access token",
      assetName: "ad account",
      testEventCode: false,
    },
  },
};

/** Narrow a string to a destination type. */
export function isDestinationType(value: string): value is DestinationType {
  return (DESTINATION_TYPES as readonly string[]).includes(value);
}

/**
 * The compact name the merchant sees on each destination's card.
 *
 * Derived from the catalog rather than hand-kept: this used to be a literal
 * map that destination.test.ts cross-checked against the Destinations page by
 * reading its source with a regex, because the two could silently disagree
 * and an email would then name a destination differently from the card the
 * merchant has to click. Both now read one object.
 */
export const DESTINATION_DISPLAY_NAMES: Record<DestinationType, string> = Object.fromEntries(
  DESTINATION_TYPES.map((t) => [t, DESTINATION_CATALOG[t].short]),
) as Record<DestinationType, string>;

/**
 * Whether a destination may email its merchant that credentials broke.
 *
 * TWO conditions, and the release one is not decoration: a `hidden`
 * destination is half-wired by definition (it exists for preview shops while
 * its live-account checks are still being done), so letting it mail a
 * merchant means telling someone to go fix a credential for a destination
 * they cannot see. A notice alone is not enough.
 *
 * Pure and entry-shaped so it can be tested against a synthetic entry. The
 * synthetic form still earns its keep now that five entries are hidden: it
 * covers the early_access and ga sides of the rule, which the live catalog
 * does not exercise for a notice-bearing hidden type.
 */
export function entryAlertsMerchant(entry: DestinationCatalogEntry): boolean {
  return entry.notice !== undefined && entry.release !== "hidden";
}

/** Types the destination-down email covers. */
export const DESTINATION_DOWN_TYPES = DESTINATION_TYPES.filter((t) =>
  entryAlertsMerchant(DESTINATION_CATALOG[t]),
);

/**
 * A destination type the down email may be built for.
 *
 * Still `Exclude<DestinationType, "ga4">`, and the reason is a judgement
 * call rather than a limitation. Two earlier versions of this comment gave
 * technical reasons that were both wrong, so here is what is actually true.
 *
 * The type CAN be derived. A mapped type over a `satisfies` literal resolves
 * both halves correctly: `notice` presence drops ga4, and `release` stays the
 * literal word written in the source, so the hidden destinations drop too. I
 * measured otherwise twice and was wrong twice; the second measurement came
 * from a malformed probe file whose unrelated compile errors degraded
 * inference. A clean probe excludes ga4 and every hidden destination exactly
 * as the runtime predicate does.
 *
 * It is not derived because of what it would cost to read. The derivation is
 * a mapped type with a conditional over an inner `satisfies` literal, plus a
 * second widened export so `catalog[t].notice` still type-checks for the one
 * entry that omits it. That is a lot of type machinery guarding a union with
 * one member's worth of drift, in a file two other modules read precisely
 * because it is simple. The runtime list is what every caller iterates, and
 * destinationDownEmail throws for a type with no notice, so the drift is
 * caught where it would do damage.
 *
 * If the gap ever widens past annoyance, the derivation works. It is written
 * up here rather than built.
 *
 * Do not treat a `Record<DestinationDownType, T>` as a list of destinations
 * that will actually be emailed. Iterate DESTINATION_DOWN_TYPES for that.
 */
export type DestinationDownType = Exclude<DestinationType, "ga4">;

// PASTE_TYPES and TEST_CODE_TYPES used to live here. Deleted: nothing read
// them. destinationDownEmail branches on `notice.kind` and reads
// `notice.testEventCode` straight off the entry, which is better than a
// precomputed list, and an exported set with tests but no consumer gives a
// false impression that something is wired to it. The catalog's own `notice`
// field is the source for both questions.

/**
 * The env var that holds a destination's dry-run kill switch.
 *
 * Every destination has one, in all four of these places, and a missing
 * entry fails differently in each:
 *   - src/config.ts, where absence means the adapter cannot read it;
 *   - .env.example, where absence means an operator never learns it exists;
 *   - vitest.config.ts and vitest.config.testdb.ts, where absence means a
 *     developer's local `X_DRY_RUN=false` turns `npm test` into live API
 *     calls against a real ad platform.
 *
 * destination-flags.test.ts asserts all four for every type, because the last
 * one is the dangerous kind of missing: the tests pass either way.
 */
export function dryRunEnvName(type: DestinationType): string {
  return `${type.toUpperCase()}_DRY_RUN`;
}

/** Destination types a merchant may use on this shop. */
export function availableDestinationTypes(isPreviewShop: boolean): DestinationType[] {
  return DESTINATION_TYPES.filter(
    (t) => DESTINATION_CATALOG[t].release !== "hidden" || isPreviewShop,
  );
}
